// ============================================================
// Reactstrap Components
// ============================================================
import {
  Col,
  Row,
  InputGroup,
  Input,
  Label,
  Form,
  FormGroup,
  InputGroupText,
  ModalBody,
} from "reactstrap";

// ============================================================
// React Hooks
// ============================================================
import { useState } from "react";

// ============================================================
// React Query - Data Fetching & Mutations
// ============================================================
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

// ============================================================
// Application UI Components
// ============================================================
import { H5, P, Btn } from "../../../../../AbstractElements";

// ============================================================
// Navigation & Icons
// ============================================================
import { Save } from "react-feather";

// ============================================================
// Notifications & Confirmation Dialogs
// ============================================================
import Swal from "sweetalert2";

// ============================================================
// Types
// ============================================================
import { CreateDeployment } from "../../../../../Types/Employee/EmployeeDeployments";
import { ViewCompanyProfile } from "../../../../../Types/CompanyProfile/CompanyProfile";
import { ViewCompanyJobs } from "../../../../../Types/CompanyProfile/CompanyJobs";
import { ViewDivision } from "../../../../../Types/CompanyProfile/Division";
import { ViewDepartments } from "../../../../../Types/CompanyProfile/Department";
import { ViewRegion } from "../../../../../Types/MasterData/Region";
import { ViewCluster } from "../../../../../Types/CompanyProfile/Cluster";
import { ViewDeploymentSite } from "../../../../../Types/CompanyProfile/DeploymentSite";

// ============================================================
// Services & Query Keys
// ============================================================
import { employeeDeploymentService } from "../../../../../Services/employeeDeployments";

import {
  VIEW_COMPANY_QUERY_KEY,
  companyprofileService,
} from "../../../../../Services/companyProfileService";

import {
  VIEW_COMPANY_JOBS_QUERY_KEY,
  companyjobTitleService,
} from "../../../../../Services/companyjobtitleService";

import {
  VIEW_DIVISION_QUERY_KEY,
  divisionService,
} from "../../../../../Services/divisionService";

import {
  VIEW_DEPARTMENT_QUERY_KEY,
  departmentService,
} from "../../../../../Services/departmentService";

import {
  VIEW_REGION_QUERY_KEY,
  regionService,
} from "../../../../../Services/regionsService";

import {
  VIEW_CLUSTER_QUERY_KEY,
  clusterService,
} from "../../../../../Services/clusterService";

import {
  VIEW_DEPSITE_QUERY_KEY,
  deploymentsiteService,
} from "../../../../../Services/deploymentsiteService";

// ============================================================
// Component Props
// ============================================================
interface EmployeeProfileProps {
  EmpNo: string;
  toggle?: () => void;
}

// ============================================================
// Create Employee Deployment Form
// ============================================================
const CreateDeploymentForm: React.FC<EmployeeProfileProps> = ({
  toggle,
  EmpNo,
}) => {
  // ==========================================================
  // State
  // ==========================================================
  const [formData, setFormData] = useState<CreateDeployment>({
    emp_no: EmpNo,
    company_code: "",
    role_id: null,
    region_id: null,
    cluster_id: null,
    division_id: null,
    department_id: null,
    deployment_site_id: null,
    effectivity_date: "",
    is_main: 1,
  });

  const queryClient = useQueryClient();

  // ============================================================================
  // Company Data
  // ============================================================================
  const { data: CompanyData, isLoading: CompanyIsLoading } = useQuery({
    queryKey: VIEW_COMPANY_QUERY_KEY,
    queryFn: () => companyprofileService.getAllViewCompanyProfile(),
    enabled: !!EmpNo,
  });

  const companycode: ViewCompanyProfile[] = Array.isArray(CompanyData?.data)
    ? CompanyData.data
    : [];

  // ============================================================================
  // Company Job Data
  // ============================================================================
  const { data: JobData, isLoading: JobIsLoading } = useQuery({
    queryKey: [VIEW_COMPANY_JOBS_QUERY_KEY, formData.company_code],
    queryFn: () =>
      companyjobTitleService.viewAllJobsByCompany(formData.company_code),
    enabled: Boolean(formData.company_code),
  });

  const jobtitles: ViewCompanyJobs[] = Array.isArray(JobData)
    ? [...JobData]
        .filter((item) => Number(item.status) === 1)
        .sort((a, b) =>
          a.job_title.localeCompare(b.job_title, undefined, {
            sensitivity: "base",
          }),
        )
    : [];

  // ============================================================================
  // Company Division Data
  // ============================================================================
  const { data: DivisionData, isLoading: DivIsLoading } = useQuery({
    queryKey: [VIEW_DIVISION_QUERY_KEY, formData.company_code],
    queryFn: () =>
      divisionService.getAllViewDivisionByCompany(formData.company_code),
    enabled: Boolean(formData.company_code),
  });

  const divisions: ViewDivision[] = Array.isArray(DivisionData)
    ? [...DivisionData]
        .filter((item) => Number(item.status) === 1)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          }),
        )
    : [];

  // ============================================================================
  // Company Department Data
  // ============================================================================
  const { data: DepartmentData, isLoading: DepIsLoading } = useQuery({
    queryKey: [VIEW_DEPARTMENT_QUERY_KEY, formData.division_id],
    queryFn: () =>
      departmentService.getAllViewDepartmentByDiv(Number(formData.division_id)),
    enabled: Boolean(formData.division_id),
  });

  const departments: ViewDepartments[] = Array.isArray(DepartmentData)
    ? [...DepartmentData]
        .filter((item) => Number(item.status) === 1)
        .sort((a, b) =>
          a.department_name.localeCompare(b.department_name, undefined, {
            sensitivity: "base",
          }),
        )
    : [];

  // ============================================================================
  // Company Region Data
  // ============================================================================
  const { data: RegionData, isLoading: RegionIsLoading } = useQuery({
    queryKey: [VIEW_REGION_QUERY_KEY, formData.company_code],
    queryFn: () => regionService.getAllRegions(),
    enabled: Boolean(formData.company_code),
  });

  const regions: ViewRegion[] = Array.isArray(RegionData?.data)
    ? [...RegionData?.data].filter((item) => Number(item.status) === 1)
    : [];

  // ============================================================================
  // Company Cluster Data
  // ============================================================================
  const { data: ClusterData, isLoading: ClusterIsLoading } = useQuery({
    queryKey: [
      VIEW_CLUSTER_QUERY_KEY,
      formData.company_code,
      formData.region_id,
    ],
    queryFn: () =>
      clusterService.getAllViewClusterByCompanyAndRegion(
        formData.company_code,
        Number(formData.region_id),
      ),
    enabled: Boolean(formData.company_code) && Boolean(formData.region_id),
  });

  const clusters: ViewCluster[] = Array.isArray(ClusterData)
    ? [...ClusterData]
        .filter((item) => Number(item.status) === 1)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          }),
        )
    : [];

  // ============================================================================
  // Company Deployment Site Data
  // ============================================================================
  const { data: DepSiteData, isLoading: DepSiteIsLoading } = useQuery({
    queryKey: [
      VIEW_DEPSITE_QUERY_KEY,
      formData.company_code,
      formData.region_id,
      formData.cluster_id,
    ],
    queryFn: () =>
      deploymentsiteService.getAllViewDeploymentSiteByCluster(
        formData.company_code,
        Number(formData.region_id),
        Number(formData.cluster_id),
      ),
    enabled:
      Boolean(formData.company_code) &&
      Boolean(formData.region_id) &&
      Boolean(formData.cluster_id),
  });

  const depsites: ViewDeploymentSite[] = Array.isArray(DepSiteData)
    ? [...DepSiteData]
        .filter((item) => Number(item.status) === 1)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          }),
        )
    : [];

  // ============================================================
  // Handle Form Input Changes
  // ============================================================
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // Create Mutation
  // ============================================================
  const createMutation = useMutation({
    mutationFn: (data: CreateDeployment) =>
      employeeDeploymentService.createEmployeeStatus(data),

    onMutate: () => {
      Swal.fire({
        title: "Processing Request",
        text: "The system is prcoessing the transaction. Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Employee record has been updated successfully.",
        confirmButtonColor: "#4e73df",
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Refreshing Employee Information",
            text: "The system is refreshing the updated employee information. Please wait...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();

              setTimeout(() => {
                Swal.close();
              }, 3000);
            },
          });
        }
      });

      if (toggle) toggle();
    },

    onError: (error: any) => {
      const responseData = error.response?.data;
      Swal.close();

      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: true,
        confirmButtonText: "OK",
        icon: "error",
        title: "Error",
        text: responseData?.message || "Failed to update employment history.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    Swal.fire({
      title: "Set Main Deployment",
      text: 'You are about to perform a CRITICAL action. This cannot be undone. Please type "Confirm" to proceed.',
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> Set Deployment',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,
      input: "text",
      inputValue: "",
      inputPlaceholder: 'Type "Confirm" to continue',
      inputAutoFocus: true,
      didOpen: () => {
        const input = Swal.getInput();
        if (input) {
          input.value = "";
          input.setAttribute("autocomplete", "off");
        }
      },
      inputValidator: (value) => {
        if (value.trim() !== "Confirm") {
          return 'Please type "Confirm" to proceed.';
        }
        return null;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Saving...",
          text: "Please wait while your activity is being saved.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => Swal.showLoading(),
        });
        createMutation.mutate(formData);
      }
    });
  };

  // ==========================================================
  // Component UI
  // ==========================================================
  return (
    <ModalBody className="large-modal-body modal-body">
      <Form className="needs-validation w-100" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Row className="p-1">
              <Col md="6">
                <FormGroup>
                  <Label>
                    Company <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-badge txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="company_code"
                      name="company_code"
                      value={formData.company_code}
                      required
                      disabled={CompanyIsLoading}
                      onChange={handleInputChange}
                    >
                      {
                        <option value="" disabled selected>
                          {CompanyIsLoading
                            ? "Loading..."
                            : "- SELECT COMPANY - "}
                        </option>
                      }
                      {!CompanyIsLoading &&
                        companycode.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.company?.toUpperCase() +
                              " (" +
                              item.status_label?.toUpperCase() +
                              ")"}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col sm="6">
                <FormGroup>
                  <Label>
                    Effectivity Date <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-ui-calendar txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="date"
                      id="effectivity_date"
                      name="effectivity_date"
                      onChange={handleInputChange}
                      required
                      value={formData?.effectivity_date || ""}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Role <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-briefcase-alt-1 txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="role_id"
                      name="role_id"
                      value={formData.role_id ?? ""}
                      required
                      disabled={JobIsLoading || !formData.company_code}
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {JobIsLoading
                          ? "Loading..."
                          : jobtitles.length === 0
                            ? "NO DATA FOUND"
                            : "- SELECT ROLE -"}
                      </option>

                      {jobtitles.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {(item.job_title ?? "").toUpperCase()} (
                          {(item.level_name ?? "").toUpperCase()})
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Region <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="region_id"
                      name="region_id"
                      value={formData.region_id ?? ""}
                      required
                      disabled={RegionIsLoading || !formData.company_code}
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {RegionIsLoading
                          ? "Loading..."
                          : regions.length === 0
                            ? "NO DATA FOUND"
                            : "- SELECT REGIONS -"}
                      </option>

                      {regions.map((item) => (
                        <option key={item.id} value={String(item.id) ?? ""}>
                          {(item.name ?? "").toUpperCase() ?? "NO DATA FOUND"}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Division <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-building-alt txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="division_id"
                      name="division_id"
                      value={formData.division_id ?? ""}
                      required
                      disabled={DivIsLoading || !formData.company_code}
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {DivIsLoading
                          ? "Loading..."
                          : divisions.length === 0
                            ? "NO DATA FOUND"
                            : "- SELECT DIVISION -"}
                      </option>

                      {divisions.map((item) => (
                        <option key={item.id} value={String(item.id) ?? ""}>
                          {(item.name ?? "").toUpperCase() ?? "NO DATA FOUND"}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Cluster <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="cluster_id"
                      name="cluster_id"
                      value={formData.cluster_id ?? ""}
                      required
                      disabled={ClusterIsLoading || !formData.region_id}
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {ClusterIsLoading
                          ? "Loading..."
                          : clusters.length === 0
                            ? "NO DATA FOUND"
                            : "- SELECT CLUSTER -"}
                      </option>

                      {clusters.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {(item.name ?? "").toUpperCase()}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Department <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-building-alt txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="department_id"
                      name="department_id"
                      value={formData.department_id ?? ""}
                      required
                      disabled={DepIsLoading || !formData.division_id}
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {DepIsLoading
                          ? "Loading..."
                          : departments.length === 0
                            ? "NO DATA FOUND"
                            : "- SELECT DEPARTMENT -"}
                      </option>

                      {departments.map((item) => (
                        <option
                          key={item.department_id}
                          value={String(item.department_id) ?? ""}
                        >
                          {(item.department_name ?? "").toUpperCase() ??
                            "NO DATA FOUND"}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Deployment Site <b className="text-danger">*</b>
                  </Label>

                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-location-pin txt-primary"></i>
                    </InputGroupText>

                    <Input
                      type="select"
                      id="deployment_site_id"
                      name="deployment_site_id"
                      value={formData.deployment_site_id ?? ""}
                      required
                      disabled={
                        DepSiteIsLoading ||
                        !formData.company_code ||
                        !formData.region_id ||
                        !formData.cluster_id
                      }
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {DepSiteIsLoading
                          ? "Loading..."
                          : depsites.length === 0
                            ? "NO DATA FOUND"
                            : "- SELECT SITE -"}
                      </option>

                      {depsites.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {(item.name ?? "").toUpperCase()}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <div className="d-flex justify-content-end">
                <Col md="6">
                  <Btn
                    color="primary"
                    className="w-100"
                    type="submit"
                    disabled={createMutation.isPending}
                  >
                    <Save size={16} className="me-1" />
                    {createMutation.isPending ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Creating...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Btn>
                </Col>
              </div>
            </Row>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateDeploymentForm;
